import { useEffect, useState } from 'react';
import orchardService from '../../services/orchard';

export interface PlaceChoice {
  key: string;
  label: string;
  group: string;
  farm_id: number | null;
  block_id: number | null;
  cultivar_area_id: number | null;
}

/** Whole business, every farm, block and cultivar area — for budget lines. */
export function useBudgetPlaces(): PlaceChoice[] {
  const [opts, setOpts] = useState<PlaceChoice[]>([]);
  useEffect(() => {
    let live = true;
    Promise.all([orchardService.getAllFarms(), orchardService.getAllBlocks(), orchardService.getAllCultivarAreas()])
      .then(([f, b, a]) => {
        if (!live) return;
        const farms = f.success ? f.data.farms : [];
        const blocks = b.success ? b.data.blocks : [];
        const areas = a.success ? a.data.areas : [];
        const list: PlaceChoice[] = [{ key: 'org', label: 'Whole business', group: 'All farms', farm_id: null, block_id: null, cultivar_area_id: null }];
        for (const farm of farms) {
          list.push({ key: `farm:${farm.id}`, label: `${farm.name} (whole farm)`, group: farm.name, farm_id: farm.id, block_id: null, cultivar_area_id: null });
          for (const blk of blocks.filter(x => x.farm_id === farm.id)) {
            list.push({ key: `block:${blk.id}`, label: `${blk.name} (whole block)`, group: farm.name, farm_id: null, block_id: blk.id, cultivar_area_id: null });
            for (const ca of areas.filter(x => x.block_id === blk.id)) {
              list.push({ key: `cultivar_area:${ca.id}`, label: `${blk.name} › ${ca.name}`, group: farm.name, farm_id: null, block_id: null, cultivar_area_id: ca.id });
            }
          }
        }
        setOpts(list);
      })
      .catch(() => { if (live) setOpts([]); });
    return () => { live = false; };
  }, []);
  return opts;
}

export function placeKeyOf(l: { farm_id: number | null; block_id: number | null; cultivar_area_id: number | null }): string {
  if (l.cultivar_area_id) return `cultivar_area:${l.cultivar_area_id}`;
  if (l.block_id) return `block:${l.block_id}`;
  if (l.farm_id) return `farm:${l.farm_id}`;
  return 'org';
}

export function placeName(l: { farm_name?: string | null; block_name?: string | null; cultivar_area_name?: string | null }): string {
  if (l.cultivar_area_name) return `${l.block_name ? `${l.block_name} › ` : ''}${l.cultivar_area_name}`;
  if (l.block_name) return `${l.block_name} (whole block)`;
  if (l.farm_name) return `${l.farm_name} (whole farm)`;
  return 'Whole business';
}

export const num = (n: number | null | undefined, dp = 1): string =>
  n == null ? '—' : (Math.round(n * 10 ** dp) / 10 ** dp).toLocaleString();

export const pct = (n: number | null | undefined): string => (n == null ? '—' : `${Math.round(n)}%`);
